import React, { Dispatch, SetStateAction, useRef, useState } from 'react';
import { TabContent } from '../../../common/sharedComponents/TabContent';
import { IMyAuditSections } from '../../../@types/myAudit';
import {
  TabContentGridContainer,
  TabsContentCommentsContainer,
} from '../../../common/sharedComponents/TabContent/TabContent.styled';
import { Grid } from '../../../common/sharedComponents/Grid';
import { QuestionCard } from '../../../myAudit/components/QuestionCard';
import { IQuestionCard } from '../../../@types/questionCard';
import { ISelectedOption } from '../../../@types/searchBox';
import { IDropdownOption } from '../../../@types/dropdown';
import { MyAuditConductComments } from './MyAuditConductComments';
import { ButtonMediaUpload } from '../../../myAudit/components/ButtonMediaUpload';
import {
  ButtonMediaUploadWrapper,
  ImageInputContainer,
} from '../../../myAudit/components/ContentTab/styled.contentTab';
import { Cropper } from '../../../common/sharedComponents/Cropper';
import { patternImage } from '../../../../utils/constants';
import { useTranslation } from 'react-i18next';

export interface IMyAuditConductTabContent extends IQuestionCard {
  section: IMyAuditSections;
  handleTaggedUsers: (taggedUser: ISelectedOption[]) => void;
  handleMyAuditComments: (comments: string) => void;
  handleRoomNumber: (roomNumber: string) => void;
  handleOutletName: (outletName: string) => void;
  handleMealPeriod: (mealPeriod: IDropdownOption) => void;
  startDate?: Date | string;
  setStartDate: Dispatch<SetStateAction<Date | string | undefined>>;
  dateTime?: Date | string;
  setDateTime: Dispatch<SetStateAction<Date | string | undefined>>;
  startDateRange?: Date | string;
  setStartDateRange: Dispatch<SetStateAction<Date | string | undefined>>;
  endDateRange?: Date | string;
  setEndDateRange: Dispatch<SetStateAction<Date | string | undefined>>;
  isLoadingImg: boolean;
  isLoadingImgDelete: boolean;
}

export function MyAuditConductTabContent ({
  section,
  onClickScore,
  handleMediaUpload,
  handleQuestionComment,
  onDelete,
  handleReplaceImage,
  questionImgLoading,
  handleTaggedUsers,
  handleMyAuditComments,
  handleRoomNumber,
  handleOutletName,
  handleMealPeriod,
  startDate,
  setStartDate,
  dateTime,
  setDateTime,
  startDateRange,
  setStartDateRange,
  endDateRange,
  setEndDateRange,
  isLoadingImg,
  isLoadingImgDelete,
}: IMyAuditConductTabContent) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isGripOpen, setIsGridOpen] = useState<boolean>(false);
  const handleImageCompletion = (croppedImage: File | null) => {
    if (handleMediaUpload) {
      handleMediaUpload(croppedImage, false, section && section.id);
    }
    setImageFile(null);
    setImageFile(null);
  };

  const getPreviousImages = () => {
    if (!section || !section.attachments) {
      return [];
    }

    return section.attachments
      .filter((attachment) => attachment.type.match(patternImage))
      .map((attachment) => attachment.name);
  };

  return (
    <TabContent title={section.name}>
      <TabContentGridContainer>
        <Grid
          media={section && section.attachments}
          isGridOpen={isGripOpen}
          handleOpenGrid={() => setIsGridOpen(!isGripOpen)}
          onDelete={onDelete}
          isLoadingImgDelete={isLoadingImgDelete}
        />
        <ButtonMediaUploadWrapper>
          <ButtonMediaUpload
            data-testid="button-upload-media"
            title={t('COMMON.MY_AUDIT.BUTTON.UPLOAD_MEDIA')}
            onClick={() => inputRef.current && inputRef.current.click()}
            loading={isLoadingImg}
          />
          <ImageInputContainer
            ref={inputRef}
            type="file"
            accept="audio/*, image/*,audio/mp3, audio/wav, audio/ogg, audio/m4a, audio/flac, audio/aac"
            onChange={(event) => {
              if (
                event &&
                event.target &&
                event.target.files &&
                event.target.files[0]
              ) {
                const newImage = event.target.files[0];
                if (newImage.type.includes('image')) {
                  setImageFile(newImage);
                } else {
                  if (handleMediaUpload) {
                    handleMediaUpload(newImage, false, section && section.id);
                  }
                }
                if (inputRef.current) {
                  inputRef.current.value = '';
                }
              }
            }}
          />
          {imageFile && (
            <Cropper
              imageFile={imageFile}
              onImageCompletion={handleImageCompletion}
              previousImages={getPreviousImages()}
            />
          )}
        </ButtonMediaUploadWrapper>
      </TabContentGridContainer>
      {section &&
        section.standards.map((standard) => {
          return (
            <QuestionCard
              key={standard.id}
              question={standard}
              onClickScore={onClickScore}
              handleMediaUpload={handleMediaUpload && handleMediaUpload}
              handleQuestionComment={handleQuestionComment}
              sectionId={section.id}
              onDelete={onDelete}
              handleReplaceImage={handleReplaceImage}
              questionImgLoading={questionImgLoading}
              showDropdownIcon={true}
            />
          );
        })}
      <TabsContentCommentsContainer>
        <MyAuditConductComments
          section={section}
          handleMyAuditComments={handleMyAuditComments}
          handleOutletName={handleOutletName}
          handleRoomNumber={handleRoomNumber}
          handleMealPeriod={handleMealPeriod}
          handleTaggedUsers={handleTaggedUsers}
          startDate={startDate}
          setStartDate={setStartDate}
          dateTime={dateTime}
          setDateTime={setDateTime}
          startDateRange={startDateRange}
          setStartDateRange={setStartDateRange}
          endDateRange={endDateRange}
          setEndDateRange={setEndDateRange}
        />
      </TabsContentCommentsContainer>
    </TabContent>
  );
}
